import React, { useState, useEffect } from 'react';
import UserAvatar from './UserAvatar';

const LogoutConfirmModal = ({ isOpen, user, loading = false, onConfirm, onCancel }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = (confirmed) => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      if (confirmed && onConfirm) {
        onConfirm();
      } else if (onCancel) {
        onCancel();
      }
    }, 200);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="logout-confirm-title"
    >
      {/* Backdrop */}
      <div
        className={`confirm-backdrop ${isClosing ? 'closing' : ''} fixed inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={() => handleClose(false)}
      />

      {/* Dialog Panel */}
      <div
        className={`confirm-content ${isClosing ? 'closing' : ''} relative w-full max-w-[360px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5`}
      >
        <h3
          id="logout-confirm-title"
          className="text-center text-base font-bold text-gray-900 dark:text-white"
        >
          Are you sure you want to log out?
        </h3>

        {/* User card */}
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5">
          <UserAvatar user={user} size="sm" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
              {user?.full_name || 'User'}
            </p>
            {user?.email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <button
          onClick={() => handleClose(true)}
          disabled={loading}
          autoFocus
          className="mt-4 w-full px-4 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500/30 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Logging out…' : 'Log out'}
        </button>
        <button
          onClick={() => handleClose(false)}
          disabled={loading}
          className="mt-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400/30 disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LogoutConfirmModal;
